<template>
    <div class="error-layout-container" :class="props.class">
        <div class="container-fluid card error-layout-card" :class="{ 'error-layout-card--small': props.small }">
            <div class="card-body text-center">
                <img :src="getIllustration()" :alt="t('errors.imageAlt')" class="img-fluid mb-4" />

                <h1 v-dompurify-html="te(props.title) ? t(props.title) : props.title" class="display-5 mb-3" />

                <p
                    v-dompurify-html="te(props.subtitle) ? t(props.subtitle) : props.subtitle"
                    class="text-secondary mb-0" />

                <div v-if="$slots.buttons" class="mt-4">
                    <slot name="buttons"> <!-- Buttons are provided by the parent component --> </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { TranslationKey } from '@/i18n';
import { useI18n } from 'vue-i18n';

import evaluationError from '@/assets/img/illustrations/evaluation-error.svg';
import womanPointingFinger from '@/assets/img/illustrations/woman-pointing-finger.svg';
import submissionComplete from '@/assets/img/illustrations/submission-complete.svg';
import getStarted from '@/assets/img/illustrations/get-started.svg';
import reportAwaited from '@/assets/img/illustrations/report-awaited.svg';

interface ErrorLayoutProps {
    title: TranslationKey | string;
    subtitle: TranslationKey | string;
    class?: string;
    illustration?: string;
    small?: boolean;
}

const props = withDefaults(defineProps<ErrorLayoutProps>(), {
    illustration: 'woman-pointing-finger',
    illustrationAltText: 'Confused person in front of a laptop',
    class: '',
    hideButtons: false,
    small: false,
});

const { t, te } = useI18n();

function getIllustration() {
    if (props.illustration === 'evaluation-error') {
        return evaluationError;
    } else if (props.illustration === 'submission-complete') {
        return submissionComplete;
    } else if (props.illustration === 'get-started') {
        return getStarted;
    } else if (props.illustration === 'report-awaited') {
        return reportAwaited;
    }
    return womanPointingFinger;
}
</script>

<style scoped lang="scss">
.error-layout-container {
    width: 100%;
}

.error-layout-card {
    width: min(100%, 600px);
    box-shadow: var(--#{$prefix}box-shadow-sm);
    margin-block-end: 0px;
    margin-inline: auto;

    &--small {
        width: min(100%, 400px);
        box-shadow: none;
        border: none;
        border-radius: 0;
        background-color: unset;

        h1 {
            font-size: 1.125rem;
        }

        img {
            max-width: 66%;
        }
    }
}

.card-body {
    padding-block: 1.5rem;
    padding-inline: 0px;

    @include container-breakpoint-up(md) {
        padding-block: 3rem;
    }
}
</style>

<style lang="scss">
// Full-page error routes render as direct children of #app (e.g. NotFoundView, TimeoutView).
// center the error layout in the middle of the screen
#app > .error-layout-container {
    /* Fallback for older browsers */
    min-height: 100vh;
    /* Adapts dynamically when the browser UI elements are present */
    min-height: 100dvh;
    display: grid;
    place-items: center;
}
</style>
